<template>
  <div v-loader="isLoading">
    <full-calendar-entity-switcher
      v-if="options.entityPickerVisibility"
      :panelId="panelId"
      :options="options"
      :config="config"
      @entities="checkEntities"
    />
    <div ref="calendar" class="vue-fullcalendar"></div>
    <full-calendar-entity-selector
      :title="'create_calendar_event'"
      :fullscreen="false"
      :is-open="isDialogOpen"
      @entity-selector-closed="handleDialogClose"
    >
      <el-alert
        :title="$i18n.t('calendar_new_event_description')"
        type="info"
        :description="`${createDate.startDate} - ${createDate.endDate}`"
        show-icon
      >
      </el-alert>
      <div class="d-flex justify-content-around flex-wrap pt-4">
        <el-button
          v-for="(entity, id) in entityPickerConfig"
          :key="id"
          icon="el-icon-plus"
          :style="`color: ${entity.styleOptions.color}`"
          :data-event-btn="entity.alias"
          @click="createEvent(entity)"
        >
          {{ $i18n.t(entity.alias) }}
        </el-button>
      </div>
    </full-calendar-entity-selector>
  </div>
</template>

<script>
import $ from 'jquery'
import 'fullcalendar'
import 'fullcalendar/dist/fullcalendar.css'

import moment from 'moment'
import notify from '../../utils/mixin-notify'
import listRest from '../../utils/mixin-list-rest'
import FullCalendarEntitySwitcher from './FullCalendarEntitySwitcher'
import FullCalendarEntitySelector from './FullCalendarEntitySelector'

import { getLanguageCode } from '../../utils'
import { mapActions, mapGetters } from 'vuex'
import { uniqueId, get, isNil, isEmpty } from 'lodash'
import { SELECTED_CLASS } from './calendar'
import { CALENDAR_VIEWS } from '../../consts'

const LOCALE_LANG = getLanguageCode()

import(`fullcalendar/dist/locale/${LOCALE_LANG}.js`)

/**
 * @class FullCalendar
 *
 * @description This component renders the calendar interface
 */
export default {
  name: 'FullCalendar',
  mixins: [listRest, notify],
  components: {
    FullCalendarEntitySelector,
    FullCalendarEntitySwitcher,
  },
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    date: {
      type: Object,
      default: () => moment(),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Array,
      default: () => [],
    },
    panelId: String,
    fieldId: String,
    dateFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    dateTimeFormat: {
      type: String,
      default: 'YYYY-MM-DD HH:mm',
    },
    isFormField: {
      type: Boolean,
      default: false,
    },
    view: String,
    minDate: String,
    maxDate: String,
  },
  data() {
    return {
      calendar: null,
      modalId: uniqueId('calendar-update-'),
      visibleEntities: [],
      startTime: '08:00',
      endTime: '09:00',
      firstRender: true,
      isDialogOpen: false,
      createDate: {
        startDate: null,
        endDate: null,
      },
      isLoading: false,
      selectedViewName: '',
      currentView: {},
    }
  },
  methods: {
    ...mapActions(['showModal']),
    ...mapActions('popup', ['setOpened', 'setUrl', 'setUrls']),
    handleDialogClose() {
      this.isDialogOpen = false
    },
    highlightSelectedDay(event) {
      if (this.selectedViewName !== CALENDAR_VIEWS.MONTH) return
      if (event.isSelected) {
        event.date = moment(event.date).format(this.dateFormat)
        const highlightedDay = document.querySelector(`.fc-day.fc-widget-content.${SELECTED_CLASS}`)

        if (highlightedDay) {
          const dateValue = highlightedDay.getAttribute('data-date')
          if (dateValue === event.date) return
          highlightedDay.classList.remove(SELECTED_CLASS)
        }
        const selectedDateCell = document.querySelector(`[data-date="${event.date}"]`)
        if (selectedDateCell) {
          selectedDateCell.classList.add(SELECTED_CLASS)
        }
      }
    },
    timeCheck(time) {
      time = moment(time)
      const hour = time.hours()
      const minute = time.minutes()
      const sec = time.seconds()
      return hour === 0 && minute === 0 && sec === 0
    },
    openPopup(url) {
      this.setUrl(url)
      this.setOpened()
    },
    async saveEvent(event) {
      this.isLoading = true

      const {
        formId: idForm,
        rowKey: idMaintable,
        startId: eventStartId,
        endId: eventEndId,
        start: eventStart,
        end: eventEnd,
      } = event

      if (!idForm || !idMaintable || !eventStartId || !eventEndId || !eventStart || !eventEnd) {
        this.isLoading = false

        const noty = {
          title: 'event_update_error',
          text: 'cant_update_event_check_it',
          type: 'error',
        }
        return await this.showNotification(noty)
      }

      const dynformData = await this.getDynformData(idForm, idMaintable)
      const items = get(dynformData, 'body.ITEMS', {})
      const fields = this.getFields(items)
      const eventInterval = {
        [eventStartId]: eventStart.format(),
        [eventEndId]: this.timeCheck(eventEnd) ? eventEnd.subtract(1, 'days').format() : eventEnd.format(),
      }
      const formData = Object.assign({}, fields, eventInterval)
      const parameters = {
        idForm,
        idMaintable,
        formData: JSON.stringify(formData),
      }

      let saveSuccess = false
      const saveResponse = await this.$client.execute({
        operationId: 'Dynform_saveData',
        method: 'post',
        parameters,
        requestContentType: 'application/x-www-form-urlencoded',
      })

      const errors = get(saveResponse, 'body.HEADER.ERROR', [])
      if (isEmpty(errors)) {
        const noty = {
          title: 'event_updated_successfully',
          text: 'event_updated_successfully_during_save',
          type: 'success',
        }
        this.showNotification(noty)
        this.$emit('event-saved', event)
        saveSuccess = true
      } else {
        const noty = {
          title: 'event_update_error',
          text: 'event_update_error_during_save',
          type: 'error',
        }
        this.showNotification(noty)
        saveSuccess = false
      }

      this.isLoading = false
      return saveSuccess
    },
    checkEntities($event) {
      this.visibleEntities = $event
      this.calendar && this.calendar.fullCalendar('rerenderEvents')
    },
    openEntityPicker(startDate, endDate) {
      this.createDate = {
        startDate: startDate,
        endDate: endDate,
      }
      if (this.config.length > 1) {
        this.isDialogOpen = true
      } else if (this.config.length === 1) {
        this.createEvent(this.config[0])
      }
    },
    setUrlParams(url, parameters) {
      parameters.forEach(({ key, value }) => (url = `${url}&${key}=${value}`))
      return url
    },
    createEvent(entity) {
      this.isDialogOpen = false
      const startDate = this.createDate.startDate
      const endDate = this.createDate.endDate
      let url = `${entity.controller}/create?${entity.startDate}=${startDate}&${entity.endDate}=${endDate}`

      const parameters = get(entity, 'parameters', [])

      if (!isEmpty(parameters)) {
        url = this.setUrlParams(url, parameters)
      }

      this.openPopup(url)
    },
    isSubFormField(field) {
      return Array.isArray(field) && !isNil(get(field, '0.ID'))
    },
    async getDynformData(idForm, idMaintable) {
      return await this.$client.execute({
        operationId: 'Dynform_getData',
        method: 'get',
        parameters: {
          idForm,
          idMaintable,
          path: get(window.CORE, 'requestRoute', ''),
        },
      })
    },
    getFields(items) {
      const fields = {}
      let value = ''

      Object.keys(items).forEach((item) => {
        value = get(items[item], 'VALUE', '')

        if (this.isSubFormField(value)) {
          const subformValue = []

          value.forEach((row) => {
            const subformRow = {}

            Object.keys(row).forEach((field) => {
              subformRow[field] = !isNil(get(row[field], 'VALUE')) ? row[field].VALUE : row[field]
            })

            subformValue.push(subformRow)
          })
          fields[item] = subformValue
        } else {
          fields[item] = value
        }
      })

      return fields
    },
  },
  computed: {
    ...mapGetters('popup', ['isOpened']),
    entityPickerConfig() {
      return this.config.filter((entity) => {
        return !entity.hasOwnProperty('createNewElement') || entity['createNewElement']
      })
    },
  },
  watch: {
    events(newEvents, oldEvents) {
      this.calendar.fullCalendar('removeEventSource', oldEvents)
      this.calendar.fullCalendar('addEventSource', newEvents)
    },
  },
  mounted() {
    this.calendar = $(this.$refs.calendar)
    this.selectedViewName = this.view
    this.$nextTick(() => {
      const vue = this
      this.calendar.fullCalendar(
        Object.assign(
          {
            timeZone: 'UTC',
            defaultDate: this.date.currentDate,
            defaultView: this.selectedViewName,
            entityPickerVisibility: true,
            displayEventEnd: true,
            selectable: true,
            editable: false,
            events: this.events,
            slotEventOverlap: get(this.options, 'allowOverlap', true),
            navLinks: !this.isFormField,
            visibleRange: {
              start: this.date.from,
              end: this.date.to,
            },
            header: {
              left: 'prevYear, prev, today, next, nextYear',
              center: 'title',
              right: this.selectedViewName,
            },
            validRange: function () {
              // csak napokat tud kezelni, pontos időt nem
              const range = {}
              if (vue.minDate) {
                range.start = moment(vue.minDate).format(vue.dateFormat)
              }
              if (vue.maxDate) {
                const max = new Date(vue.maxDate)
                max.setDate(max.getDate() + 1)
                range.end = moment(max).format(vue.dateFormat)
              }
              return range
            },
            viewRender: (view, element) => {
              const viewStart = moment(view.start).startOf('day')
              const viewEnd = moment(view.end).startOf('day')

              if (this.firstRender) {
                this.firstRender = false
              } else {
                window.localStorage.setItem(`${this.panelId || this.fieldId}-calendar`, view.name)
              }
              const storedView = window.localStorage.getItem(`${this.panelId || this.fieldId}-calendar`)
              if (storedView) {
                this.calendar.fullCalendar('changeView', storedView)
              }
              vue.currentView = view
              vue.selectedViewName = view.name
              this.$emit('calendar-date-changed', { from: viewStart.format(this.dateFormat), to: viewEnd.format(this.dateFormat) })
            },
            eventClick: (calEvent, jsEvent, view) => {
              if (this.isFormField) return
              if (calEvent.href && calEvent.href !== '') {
                if (this.options.isPopup) {
                  this.openPopup(`${calEvent.href}?${calEvent.idName}=${calEvent.rowKey}`)
                } else {
                  window.location = `${window.location.origin}${calEvent.href}?${calEvent.idName}=${calEvent.rowKey}`
                }
              } else {
                if (this.options.isPopup) {
                  this.openPopup(`${calEvent.controller}/update?${calEvent.idName}=${calEvent.rowKey}`)
                } else {
                  window.location = `${window.location.origin}/${calEvent.controller}/update?${calEvent.idName}=${calEvent.rowKey}`
                }
              }
            },
            eventMouseover: (calEvent, jsEvent, view) => {
              $(jsEvent.currentTarget).addClass('hover')
            },
            eventMouseout: (calEvent, jsEvent, view) => {
              $(jsEvent.currentTarget).removeClass('hover')
            },
            select: function (startDate, endDate) {
              let startDateTime = startDate.hasTime()
                ? startDate.format(vue.dateTimeFormat)
                : `${startDate.format()} ${vue.startTime}`
              const endDateTime = endDate.hasTime()
                ? endDate.format(vue.dateTimeFormat)
                : `${endDate.subtract(1, 'days').format()} ${vue.endTime}`

              if (vue.isFormField) {
                if (vue.selectedViewName === CALENDAR_VIEWS.MONTH) {
                  startDateTime = moment(startDateTime).format(vue.dateFormat)
                }

                vue.highlightSelectedDay({ isSelected: true, date: startDateTime })

                vue.$emit('date-selected', startDateTime)
              } else {
                vue.openEntityPicker(startDateTime, endDateTime)
              }
            },
            eventDrop: async function (event, delta, revertFunc) {
              const isEventSaved = await vue.saveEvent(event)
              if (!isEventSaved) revertFunc()
            },
            eventResize: async function (event, delta, revertFunc) {
              const isEventSaved = await vue.saveEvent(event)
              if (!isEventSaved) revertFunc()
            },
            eventRender(event, element, view) {
              if (vue.isFormField) vue.highlightSelectedDay(event)
              return get(vue, `visibleEntities[${event.entityIndex}].active`, true)
            },
            views: {
              month: {
                timeFormat: 'H:mm',
              },
            },
          },
          this.options,
        ),
      )

      this.$emit('onCalendarInitDone', this.calendar)
      const fcPrevButton = document.querySelector('.fc-next-button')
      const fcNextButton = document.querySelector('.fc-prev-button')
      const fcNextYearButton = document.querySelector('.fc-nextYear-button')
      const fcPrevYearButton = document.querySelector('.fc-prevYear-button')

      const buttons = [fcPrevButton, fcNextButton, fcNextYearButton, fcPrevYearButton]

      buttons.forEach((button) => {
        if (!button) return
        button.addEventListener('click', () => {
          if (!this.calendar) return
          const calendar = this.calendar.fullCalendar('getCalendar')
          const start = get(calendar, 'view.start._d')
          const end = get(calendar, 'view.end._d')

          const from = moment(start).format(this.dateFormat)
          const to = moment(end).format(this.dateFormat)
          this.$emit('calendar-date-changed', { from, to })
        })
      })

      const fcMonthButton = document.querySelector('.fc-month-button')
      const fcWeekButton = document.querySelector('.fc-agendaWeek-button')
      const fcDayButton = document.querySelector('.fc-agendaDay-button')
      const fcListButton = document.querySelector('.fc-listWeek-button')

      const views = [
        { name: 'month', button: fcMonthButton },
        { name: 'agendaWeek', button: fcWeekButton },
        { name: 'agendaDay', button: fcDayButton },
        { name: 'listWeek', button: fcListButton },
      ]

      views.forEach((view) => {
        if (!view || !view.button || !view.name) return
        view.button.addEventListener('click', () => {
          const date = this.calendar.fullCalendar('getView').dateProfile.currentUnzonedRange

          const from = moment(date.startMs).format(this.dateFormat)
          const to = moment(date.endMs).format(this.dateFormat)
          this.$emit('view-changed', { type: view.name, from, to })
        })
      })
    })
  },
  beforeDestroy() {
    this.calendar.fullCalendar('destroy')
  },
}
</script>

<style lang="scss">
.fc-event.fc-time-grid-event.highlighted {
  border-style: dashed !important;
  border-width: medium !important;
}
.fc-day.fc-widget-content.selected {
  background-color: $fc-highlighted-background-color;
  opacity: 0.3;
}
.modal .expanded-row.calendar {
  box-shadow: none;
  border: none;
}
.fc-listWeek-view *:not(.fc-list-empty),
.fc-listWeek-view *:not(.fc-list-empty):before,
.fc-listWeek-view *:not(.fc-list-empty):after {
  height: auto !important;
}
.fc-scroller {
  height: auto !important;
}
.fc-list-empty {
  height: 25vh;
}
@media screen and (min-width: 1920px) {
  .fc-list-empty {
    height: 15vh;
  }
}
</style>
